import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useBlocker } from "react-router-dom";
import api from '../../api/axios';
import { AutoComplete } from 'primereact/autocomplete';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { InputNumber } from 'primereact/inputnumber';
import { Checkbox } from 'primereact/checkbox';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { Toast } from 'primereact/toast';
import { Dialog } from 'primereact/dialog';
import priceTierService from "../../services/PriceTierService";
import productService from "../../services/ProductService";
import { Dropdown } from "primereact/dropdown";

export default function ProductsAddPage() {

  const navigate = useNavigate();
  const apiUrl = process.env.REACT_APP_API_URL;
  const toast = useRef(null);

  // FORM STATE
  const [productId, setProductId] = useState(0);
  const [name, setName] = useState('');
  const [brandName, setBrandName] = useState('');
  const [category, setCategory] = useState(null);
  const [tags, setTags] = useState('');

  const [salePrice, setSalePrice] = useState(null);
  const [salePriceIsVatIncluded, setSalePriceIsVatIncluded] = useState(false);
  const [taxRate, setTaxRate] = useState(0);

  const [barcode, setBarcode] = useState(''); 
  const [unitType, setUnitType] = useState(null);
  const [trackExpirationDate, setTrackExpirationDate] = useState(false);
  const [criticalStockQuantity, setCriticalStockQuantity] = useState(0);
  const [currentStockQuantity, setCurrentStockQuantity] = useState(0);
  const [description, setDescription] = useState('');

  const [isDirty, setIsDirty] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [categories, setCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [priceTiers, setPriceTiers] = useState([]);
  const [tierPrices, setTierPrices] = useState({});
 
  const [calcExclVat, setCalcExclVat] = useState(null);
  const [calcInclVat, setCalcInclVat] = useState(null);

  const [unitTypes, setUnitTypes] = useState([]);
  const [filteredUnitTypes, setFilteredUnitTypes] = useState([]);  
 useEffect(() => {
  api.get(apiUrl + "Lookups/unit-types")
    .then(res => {
      const arr = res.data?.data ?? res.data ?? []; 
      const list = arr.map(x => ({
        label: x.name,   
        value: x.id    
      }));

      setUnitTypes(list);
      setFilteredUnitTypes(list);

      // Otomatik birim setleme (Sadece yeni ürünse)
      if (productId === 0 && list.length > 0) {
        const defaultUnit = list.find(u => u.value === 1) || list[0];
        setUnitType(defaultUnit);
      }
    })
    .catch(() => {});
}, [apiUrl, productId]);

const searchUnitType = (e) => {
  const q = (e.query || "").toLowerCase();
  const filtered = unitTypes.filter(u =>
    (u.label || "").toLowerCase().includes(q)
  );
  setFilteredUnitTypes(filtered);
}; 

  const nameRef = useRef(null);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isDirty && !isSubmitting) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty, isSubmitting]);

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      isDirty && !isSubmitting && currentLocation.pathname !== nextLocation.pathname
  );

  const recalcVatValues = (price = salePrice, vat = taxRate, included = salePriceIsVatIncluded) => {
    if (price == null || vat == null) return;

    let excl, incl;

    if (included) {
      // Fiyat KDV dahil → hariç hesaplanır
      incl = price;
      excl = price / (1 + vat / 100);
    } else {
      // Fiyat KDV hariç → dahil hesaplanır
      excl = price;
      incl = price * (1 + vat / 100);
    }

    setCalcExclVat(excl);
    setCalcInclVat(incl);
  };
  useEffect(() => {
    if (nameRef.current) {
      nameRef.current.focus();
    }
    api.get(apiUrl + 'Category/combo-categories')
      .then(res => {
        setCategories(res.data);
        setFilteredCategories(res.data);
      });
    
    // Fetch price tiers
    priceTierService.getAll()
      .then(res => {
        setPriceTiers(res.data?.data ?? res.data ?? []);
      })
      .catch(err => console.error("Fiyat seviyeleri alınamadı", err));
  }, [apiUrl]);

  const filterItems = (arr = [], query = "") =>
    arr.filter(x =>
      (x.label || "").toLowerCase().includes((query || "").toLowerCase())
    );

  const searchCategory = (e) => {
  const filtered = filterItems(categories, e.query);
  setFilteredCategories(enrichCategories(filtered, e.query));
  }; 

  const enrichCategories = (list = [], query = "") => {
  if (!query) return list;

  const exists = list.some(
    c => c.label.toLowerCase() === query.toLowerCase()
  );

  if (!exists) {
    return [
      ...list,
      {
        label: `"${query}" adlı yeni kategori ekle`,
        value: "__create__",
        rawName: query,
        isCreate: true
      }
    ];
  }

  return list;
};
  // SUBMIT
  const handleSubmit = React.useCallback(async (e) => {
    if (e) e.preventDefault();

    const payload = {
      ProductDto: {
        Id: productId,
        Name: name,
        BrandName: brandName,
        CategoryId: category?.value,
        Tags: tags,
        SalePrice: salePrice,
        SalePriceIsVatIncluded: salePriceIsVatIncluded,
        TaxRate: taxRate,
        Barcode: barcode,
        UnitTypeId: unitType?.value,
        CriticalStockQuantity: criticalStockQuantity,
        CurrentStockQuantity: currentStockQuantity,
        TrackExpirationDate: trackExpirationDate,
        Description: description,
        TenantId: 1,
        UserId: 1
      }
    };

    setIsSubmitting(true);
    try {
      let success = false;
      let targetProductId = productId;
      if (productId === 0) {
        const res = await api.post(apiUrl + "product/create", payload);
        const newId = res.data?.data || res.data;
        if (newId) {
          setProductId(newId);
          targetProductId = newId;
          success = true;
        }
      } else {
        await api.post(apiUrl + "product/update", payload);
        success = true;
      }
      
      if (success && targetProductId > 0) {
        // Save price tiers
        const pricesPayload = Object.keys(tierPrices).map(tierId => {
          const incl = tierPrices[tierId]?.priceInclVat ?? 0;
          const excl = incl / (1 + taxRate / 100);
          return {
            priceTierId: parseInt(tierId),
            priceInclVat: incl,
            priceExclVat: excl
          };
        });
        await productService.savePrices(targetProductId, pricesPayload);
        
        toast.current.show({
          severity: 'success',
          summary: 'Başarılı',
          detail: productId === 0 ? 'Ürün kaydedildi' : 'Ürün güncellendi',
          life: 2500
        });
        
        setIsDirty(false);
      }
      return success;
    } catch (err) {
      alert("Hata: " + (err.response?.data?.message || err.message));
      return false;
    } finally {
      setIsSubmitting(false);
    }
  }, [productId, name, brandName, category, tags, salePrice, salePriceIsVatIncluded, taxRate, barcode, unitType, trackExpirationDate, criticalStockQuantity, currentStockQuantity, description, apiUrl, tierPrices]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "F2") {
        e.preventDefault();
        handleSubmit();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleSubmit]);

  return (
    <>
      {/* PREMIUM DESIGN UPDATED */}
      <style>
        {`
          body {
            background-color: #f5f6fa !important;
          }
          .dark body {
            background-color: #0f172a !important;
          }

          .section-container {
            display: flex;
            gap: 20px;
            margin-bottom: 20px;
          }

          .section-box {
            flex: 1;
            padding: 20px;
            border-radius: 12px;
            background: #ffffff;
            box-shadow: 0 2px 8px rgba(0,0,0,0.06);
            transition: all .25s ease;
            border: 1px solid #e2e8f0;
          }

          .dark .section-box {
            background: #1e293b !important;
            border-color: #334155 !important;
            box-shadow: 0 4px 20px rgba(0,0,0,0.15) !important;
          }

          .section-box:hover {
            transform: translateY(-3px);
            box-shadow: 0 4px 14px rgba(0,0,0,0.12);
          }

          .section-header {
            font-size: 1.05rem;
            font-weight: 700;
            display: flex;
            align-items: center;
            margin-bottom: 18px;
            color: #334155;
          }

          .dark .section-header {
            color: #ffffff !important;
          }

          .section-header i {
            font-size: 1.3rem;
            margin-right: 10px;
            color: #6366f1;
          }

          .field-label {
            font-size: .85rem;
            font-weight: 600;
            color: #475569;
            margin-bottom: 6px;
            display: block;
          }

          .dark .field-label {
            color: #cbd5e1 !important;
          }

          .field-item {
            margin-bottom: 16px;
            display: flex;
            flex-direction: column;
          }
            .kb-toast {
              top: 80px !important; 
            }
        `}
      </style>
 <Toast ref={toast} position="top-right" className="kb-toast"/>
      <Card
        title={
          <div className="flex justify-content-between align-items-center">
            <span style={{ fontSize: "1.2rem", fontWeight: "600" }}>{productId > 0 ? "Ürün Düzenle" : "Yeni Ürün Ekle"}</span>
            <div className="flex gap-2">
              <Button label="Ürün Listesi" icon="pi pi-arrow-left" className="p-button-secondary" onClick={() => navigate("/products")} />
              <Button form="product-form" label={productId > 0 ? "Güncelle" : "Kaydet(F2)"} icon="pi pi-check" className="p-button-success" type="submit" />
            </div>
          </div>
        }
      >

       

        <Dialog
          header="Değişiklikleriniz kaydedilsin mi?"
          visible={blocker.state === "blocked"}
          style={{ width: '450px' }}
          modal
          onHide={() => blocker.reset()}
          footer={
            <div className="flex justify-end gap-2">
              <Button label="Vazgeç" icon="pi pi-times" className="p-button-text" onClick={() => blocker.reset()} />
              <Button label="Hayır" icon="pi pi-trash" className="p-button-danger p-button-text" onClick={() => blocker.proceed()} />
              <Button label="Evet" icon="pi pi-check" className="!bg-emerald-600 border-none" autoFocus onClick={async () => {
                const success = await handleSubmit();
                if (success) {
                  blocker.proceed();
                }
              }} />
            </div>
          }
        >
          <div className="flex align-items-center gap-3">
            <i className="pi pi-exclamation-triangle text-yellow-500 text-4xl" />
            <p className="m-0">
              Sayfadan ayrılmadan önce yaptığınız değişiklikleri kaydetmek ister misiniz?
            </p>
          </div>
        </Dialog>

        <form id="product-form" onSubmit={handleSubmit}>

          {/* ----------------------------- */}
          {/* BÖLÜM 1 - ÜRÜN BİLGİLERİ */}
          {/* ----------------------------- */}
          <div className="section-container">

            <div className="section-box">
              <div className="section-header">
                <i className="pi pi-box"></i> Ürün Bilgileri
              </div>

              <div className="field-item">
                <label className="field-label">Ürün Adı</label>
                <InputText 
                ref={nameRef}
                value={name} 
                onChange={(e) => { setName(e.target.value); setIsDirty(true); }}
                required />
              </div>

              <div className="field-item">
                <label className="field-label">Marka</label>
                <InputText value={brandName} onChange={(e) => { setBrandName(e.target.value); setIsDirty(true); }} />
              </div> 
                <div className="field-item">
                <label className="field-label">Kategori</label>
                <AutoComplete
  value={category}
  suggestions={filteredCategories}
  completeMethod={searchCategory}
  field="label"
  dropdown
  appendTo={document.body}
  itemTemplate={(item) =>
  item.isCreate ? (
    <div className="flex items-center gap-2 text-blue-600 font-bold">
      <i className="pi pi-plus-circle"></i>
      {item.label}
    </div>
  ) : (
    <span>{item.label}</span>
  )
}
  onChange={async (e) => {
    const selected = e.value;

    // 🆕 Yeni kategori ekleme
    if (selected?.isCreate) {
      try {
        const res = await api.post(apiUrl + "Category/create", {
          name: selected.rawName
        });

        debugger;
        const newCategory = {
          label: res.data.data.name,
          value: res.data.data.id
        };

        // kategori listesine ekle
        setCategories(prev => [...prev, newCategory]);
        setCategory(newCategory);

        toast.current?.show({
          severity: "success",
          summary: "Kategori eklendi",
          detail: `"${newCategory.label}" oluşturuldu`,
          life: 2000
        });
      } catch (err) {
        toast.current?.show({
          severity: "error",
          summary: "Hata",
          detail: "Kategori eklenemedi",
          life: 3000
        });
      }

      return;
    }

    // normal seçim
    setCategory(selected);
    setIsDirty(true);
  }}
/>

              </div>

              <div className="field-item">
                <label className="field-label">Etiketler</label>
                <InputText value={tags} onChange={(e) => { setTags(e.target.value); setIsDirty(true); }} placeholder="virgülle ayırın" />
              </div>
               <div className="field-item">
                <label className="field-label">Açıklama</label>
                <InputTextarea
                  value={description}
                  onChange={(e) => { setDescription(e.target.value); setIsDirty(true); }}
                  rows={3}
                />
              </div>
            </div>

            {/* ----------------------------- */}
            {/* BÖLÜM 2 - FİYAT & KDV */}
            {/* ----------------------------- */}
            <div className="section-box">
              <div className="section-header">
                <i className="pi pi-wallet"></i> Fiyat & Vergi
              </div>

              <div className="field-item">
                <label className="field-label">Satış Fiyatı</label>
                <InputNumber
                  value={salePrice}
                  onValueChange={(e) => {
                    setSalePrice(e.value);
                    recalcVatValues(e.value, taxRate, salePriceIsVatIncluded);
                    setIsDirty(true);
                   }
                  }
                  mode="currency"
                  currency="TRY"
                  locale="tr-TR"
                  required
                />
              </div>

              <div
  className={`mt-3 mb-3 p-3 rounded-xl border flex items-center justify-between transition-all
    ${salePriceIsVatIncluded
      ? "!bg-emerald-50 border-emerald-200 dark:!bg-emerald-950/20 dark:border-emerald-800/40"
      : "!bg-blue-50 border-blue-200 dark:!bg-blue-950/20 dark:border-blue-800/40"}`}
>
  <div className="flex items-center gap-3">
    <i
      className={`pi ${
        salePriceIsVatIncluded ? "pi-check-circle text-emerald-600 dark:text-emerald-400" : "pi-calculator text-blue-600 dark:text-blue-400"
      } text-xl`}
    />
    <div>
      <div className={`text-sm font-black ${
        salePriceIsVatIncluded ? "text-slate-800 dark:text-emerald-300" : "text-slate-800 dark:text-blue-300"
      }`}>
        {salePriceIsVatIncluded ? "KDV Dahil Fiyat" : "KDV Hariç Fiyat"}
      </div>
      <div className={`text-xs ${
        salePriceIsVatIncluded ? "text-slate-500 dark:text-emerald-400/70" : "text-slate-500 dark:text-blue-400/70"
      }`}>
        {salePriceIsVatIncluded
          ? "Girilen fiyat KDV içerir"
          : "KDV sonradan hesaplanır"}
      </div>
    </div>
  </div>

  <Checkbox
    checked={salePriceIsVatIncluded}
    onChange={(e) => {
      setSalePriceIsVatIncluded(e.checked);
      recalcVatValues(salePrice, taxRate, e.checked);
      setIsDirty(true);
    }}
  />
</div>

              <div className="field-item">
                <label className="field-label">KDV Oranı</label>
                <InputNumber
                  value={taxRate}
                  onValueChange={(e) => {
                     setTaxRate(e.value);
                     recalcVatValues(salePrice, e.value, salePriceIsVatIncluded);
                     setIsDirty(true);
                  }}
                  suffix="%"
                  required
                />
              </div>
            
                 <div className="grid grid-cols-2 gap-3 mt-4">
  {/* KDV HARİÇ */}
  <div
    className={`p-3 rounded-xl border transition-all ${
      !salePriceIsVatIncluded
        ? "supplier-card-blue shadow"
        : "supplier-card-slate"
    }`}
  >
    <div className={`text-[10px] font-black uppercase tracking-widest ${
      !salePriceIsVatIncluded
        ? "text-blue-600 dark:text-blue-400/80"
        : "text-slate-500 dark:text-slate-400"
    }`}>
      KDV Hariç
    </div>
    <div className="text-lg font-black text-slate-800 dark:text-white mt-1">
      {calcExclVat?.toLocaleString("tr-TR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 5
      })} ₺
    </div>
 
  </div>

  {/* KDV DAHİL */}
  <div
    className={`p-3 rounded-xl border transition-all ${
      salePriceIsVatIncluded
        ? "supplier-card-emerald shadow"
        : "supplier-card-slate"
    }`}
  >
    <div className={`text-[10px] font-black uppercase tracking-widest ${
      salePriceIsVatIncluded
        ? "text-emerald-600 dark:text-emerald-400/80"
        : "text-slate-500 dark:text-slate-400"
    }`}>
      KDV Dahil (%{taxRate})
    </div>
    <div className="text-lg font-black text-slate-800 dark:text-white mt-1">
      {calcInclVat?.toLocaleString("tr-TR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 5
      })} ₺
    </div>
 
  </div>
</div>

              {/* Çoklu Fiyat Seviyeleri */}
              <div className="mt-3 p-2 bg-slate-100  border-t border-slate-200 dark:border-slate-700">
                <h3 className="text-sm font-bold text-slate-800 dark:text-slate-200 mb-3 flex items-center gap-2">
                  <i className="pi pi-tags !text-indigo-500"></i>
                  Çoklu Fiyat Seviyeleri
                </h3>
                <div className="flex flex-col gap-3">
                  {priceTiers.map(tier => {
                    const priceVal = tierPrices[tier.id]?.priceInclVat ?? null;
                    const priceExcl = priceVal ? priceVal / (1 + taxRate / 100) : 0;
                    return (
                      <div key={tier.id} className="p-3 bg-slate-300  dark:bg-slate-800/40 rounded-xl border border-slate-100 dark:border-slate-800/60 flex items-center justify-between gap-4">
                        <div className="flex-1">
                          <div className="text-s font-bold text-slate-700 dark:text-slate-300">{tier.name}</div>
                          {tier.description && <div className="text-[10px] text-slate-400 mt-0.5">{tier.description}</div>}
                        </div>
                        <div className="w-48">
                          <InputNumber
                            value={priceVal}
                            onValueChange={(e) => {
                              setTierPrices(prev => ({
                                ...prev,
                                [tier.id]: { priceInclVat: e.value }
                              }));
                              setIsDirty(true);
                            }}
                            mode="currency"
                            currency="TRY"
                            locale="tr-TR"
                            placeholder="0"
                            className="w-full"
                            inputClassName="w-full text-right font-semibold"
                          />
                          {priceVal > 0 && (
                            <div className="text-[9px] text-right text-slate-400 mt-1">
                              KDV Hariç: {priceExcl.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  {priceTiers.length === 0 && (
                    <div className="text-xs text-slate-400 italic flex items-center gap-1.5">
                      <i className="pi pi-info-circle"></i>
                      Tanımlı fiyat seviyesi bulunamadı. Ayarlar menüsünden fiyat seviyesi ekleyebilirsiniz.
                    </div>
                  )}
                </div>
              </div>

            </div>

            {/* ----------------------------- */}
            {/* BÖLÜM 3 - DİĞER ALANLAR */}
            {/* ----------------------------- */}
            <div className="section-box">
              <div className="section-header">
                <i className="pi pi-cog"></i> Diğer Bilgiler
              </div>
              <div className="field-item">
                <label className="field-label">Açılış Stoku</label>
                <InputNumber
                  value={currentStockQuantity}
                  onValueChange={(e) => { setCurrentStockQuantity(e.value); setIsDirty(true); }}
                />
              </div>
              <div className="field-item">
                <label className="field-label">Kritik Stok</label>
                <InputNumber
                  value={criticalStockQuantity}
                  onValueChange={(e) => { setCriticalStockQuantity(e.value); setIsDirty(true); }}
                />
              </div>
              <div className="field-item">
                <label className="field-label">Barkod</label>
                <InputText value={barcode} onChange={(e) => { setBarcode(e.target.value); setIsDirty(true); }} />
              </div>

              <div className="field-item">
                <label className="field-label">Son Kullanma Tarihi Takibi</label>
                <div className="flex align-items-center">
                  <Checkbox
                    checked={trackExpirationDate}
                    onChange={(e) => {
                      setTrackExpirationDate(e.checked);
                      setIsDirty(true);
                    }}
                  />
                  <span className="text-slate-700 dark:text-slate-300 font-semibold" style={{ marginLeft: 8 }}>Evet, SKT takibi yapılsın</span>
                </div>
              </div>
  <div className="field-item">
    <label className="field-label">
      Satış Birimi <span style={{ color: "#94a3b8", fontWeight: 500 }}></span>
    </label>
  
    <AutoComplete
       value={unitType}
      suggestions={filteredUnitTypes}
      completeMethod={searchUnitType}
      onChange={(e) => { setUnitType(e.value); setIsDirty(true); }}
      field="label"
      dropdown
      appendTo={document.body}
      placeholder="Birim seçiniz..." 
    />
  
    <small style={{ color: "#64748b", marginTop: 6 }}>
      Örn: Un → kg · Zeytinyağı → lt · Çikolata → adet
    </small>
  </div>   
             
            </div>

          </div>

        </form>
      </Card>
    </>
  );
}
